import {
  cart,
  intersectLines,
  isInsideTriangle,
  isTriangleCollinear,
  TriangulatedPoint,
} from '@asset-viz/utils/fastMath'
import { convertCoordsToUVs } from '@asset-viz/utils/helpers'
import { useMemo } from 'react'
import { BufferGeometry } from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentPath,
  GeckoComponentRect,
} from 'types'

import { addUVSegmentAndWrapProperly } from './clippingUtils'
import { useSurfaceProps } from './SurfaceProvider'

const uvCoordinateTolerance = 1e-10

/**
 * Takes in a list of shapes where a shape i starts at (inclusive) index shapeIndexes[i - 1 ?? 0]
 * and ends at (exclusive) shapeIndexes[i], and returns a list of line segments, where the segments
 * associated with polygon i start at (inclusive) index shapeIndexes[i - 1 ?? 0] * 6 and end at
 * (exclusive) shapeIndexes[i] * 6, that represent the 3D projection of the shape onto the geometry,
 * ignoring anything outside the boundary.
 */
export const useSurfaceLines = (
  paths: GeckoComponentPath[],
  createBoundaryLinesIn2D: boolean = false
): { xyzs: Array<number>; indexes: ReadonlyArray<number> } | undefined => {
  const { geometry, bounds, wrapMode } = useSurfaceProps()
  const wrapsX = useMemo(
    () =>
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX,
    [wrapMode]
  )
  const wrapsY = useMemo(
    () =>
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY,
    [wrapMode]
  )
  const createBoundaryLines = useMemo(
    () =>
      createBoundaryLinesIn2D &&
      (wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
        wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX),
    [wrapMode, createBoundaryLinesIn2D]
  )
  return useMemo(() => {
    // Use a for loop over all of the triangles for efficiency's sake
    uvs.length = 0

    if (paths.length === 0) return undefined

    const uvShapeIndexes = new Array<number>()

    for (const path of paths) {
      convertAndAppendPathToUvs(
        path,
        bounds,
        wrapsX,
        wrapsY,
        createBoundaryLines,
        uvs
      )
      uvShapeIndexes.push(uvs.length)
    }

    const xyzs: number[] = []

    createMeshLineSegment(geometry, uvs, uvShapeIndexes, xyzs)
    return { xyzs, indexes: uvShapeIndexes }
  }, [paths, geometry, bounds, wrapsX, wrapsY, createBoundaryLines])
}
/** INTERNAL FUNCTION. Exported for testing purposes only. */
export const createMeshLineSegment = (
  geometry: BufferGeometry,
  uvSegment: number[],
  shapeIndexes: number[],
  outputArray: number[]
): void => {
  const inputUVs = geometry.getAttribute('uv')
  if (!inputUVs) throw new Error('No "uv" attribute!')

  const inputPositions = geometry.getAttribute('position')
  if (!inputPositions) throw new Error('No "position" attribute!')

  const pointCount = inputUVs.array.length / 2
  if (pointCount !== inputPositions.array.length / 3)
    throw new Error(
      `'uv' Vec2 count (${pointCount}) does not match ` +
        `'position' Vec3 count (${inputPositions.array.length / 3})!`
    )

  // If the geometry has an index buffer, use it. Otherwise, use the inputUVs array length
  const index = geometry.getIndex() ?? {
    count: inputUVs.array.length / 2,
    getX: (i: number) => i,
  }

  let startIndex = 0
  for (let j = 0; j < shapeIndexes.length; j++) {
    for (let i = startIndex; i < shapeIndexes[j]; i += 4) {
      const sx = uvSegment[i]
      const sy = uvSegment[i + 1]
      const ex = uvSegment[i + 2]
      const ey = uvSegment[i + 3]

      const lineMaxU = Math.max(sx, ex)
      const lineMaxV = Math.max(sy, ey)
      const lineMinU = Math.min(sx, ex)
      const lineMinV = Math.min(sy, ey)

      // Loop through indexed triangles instead of raw vertices
      for (let t = 0; t < index.count; t += 3) {
        const tpa = index.getX(t)
        const tpb = index.getX(t + 1)
        const tpc = index.getX(t + 2)

        const au = inputUVs.array[tpa * 2]
        const av = inputUVs.array[tpa * 2 + 1]

        const bu = inputUVs.array[tpb * 2]
        const bv = inputUVs.array[tpb * 2 + 1]

        const cu = inputUVs.array[tpc * 2]
        const cv = inputUVs.array[tpc * 2 + 1]

        const maxU = Math.max(au, bu, cu)
        const maxV = Math.max(av, bv, cv)
        const minU = Math.min(au, bu, cu)
        const minV = Math.min(av, bv, cv)

        if (
          // check if triangle and line segment have any overlap
          !(
            lineMinU <= maxU &&
            lineMaxU >= minU &&
            lineMinV <= maxV &&
            lineMaxV >= minV
          )
        )
          continue

        const ax = inputPositions.array[tpa * 3]
        const ay = inputPositions.array[tpa * 3 + 1]
        const az = inputPositions.array[tpa * 3 + 2]

        const bx = inputPositions.array[tpb * 3]
        const by = inputPositions.array[tpb * 3 + 1]
        const bz = inputPositions.array[tpb * 3 + 2]

        const cx = inputPositions.array[tpc * 3]
        const cy = inputPositions.array[tpc * 3 + 1]
        const cz = inputPositions.array[tpc * 3 + 2]

        if (
          isTriangleCollinear(
            ax,
            ay,
            az,
            bx,
            by,
            bz,
            cx,
            cy,
            cz,
            uvCoordinateTolerance
          )
        )
          continue

        const startPoint = cart(sx, sy, au, av, bu, bv, cu, cv)
        const endPoint = cart(ex, ey, au, av, bu, bv, cu, cv)

        const startInside = isInsideTriangle(startPoint)
        const endInside = isInsideTriangle(endPoint)

        const ab = intersectLines(au, av, bu, bv, sx, sy, ex, ey)
        const bc = intersectLines(bu, bv, cu, cv, sx, sy, ex, ey)
        const ca = intersectLines(cu, cv, au, av, sx, sy, ex, ey)

        const intersectsWithAB = ab >= 0.0
        const intersectsWithBC = bc >= 0.0
        const intersectsWithCA = ca >= 0.0

        let segmentStart: TriangulatedPoint | undefined = undefined
        let segmentEnd: TriangulatedPoint | undefined = undefined

        if (startInside) segmentStart = startPoint

        if (endInside) {
          if (!segmentStart) segmentStart = endPoint
          else segmentEnd = endPoint
        }

        if (intersectsWithAB) {
          const abIntersectPoint = { a: 1 - ab, b: ab, c: 0, x: ex, y: ey }
          if (!segmentStart) segmentStart = abIntersectPoint
          else if (
            !segmentEnd &&
            !equalPoints(
              segmentStart.a,
              segmentStart.b,
              segmentStart.c,
              abIntersectPoint.a,
              abIntersectPoint.b,
              abIntersectPoint.c
            )
          )
            segmentEnd = abIntersectPoint
        }

        if (intersectsWithBC) {
          const bcIntersectPoint = { a: 0, b: 1 - bc, c: bc, x: ex, y: ey }
          if (!segmentStart) segmentStart = bcIntersectPoint
          else if (
            !segmentEnd &&
            !equalPoints(
              segmentStart.a,
              segmentStart.b,
              segmentStart.c,
              bcIntersectPoint.a,
              bcIntersectPoint.b,
              bcIntersectPoint.c
            )
          )
            segmentEnd = bcIntersectPoint
        }

        if (intersectsWithCA) {
          const caIntersectPoint = { a: ca, b: 0, c: 1 - ca, x: ex, y: ey }
          if (!segmentStart) segmentStart = caIntersectPoint
          else if (
            !segmentEnd &&
            !equalPoints(
              segmentStart.a,
              segmentStart.b,
              segmentStart.c,
              caIntersectPoint.a,
              caIntersectPoint.b,
              caIntersectPoint.c
            )
          )
            segmentEnd = caIntersectPoint
        }

        if (segmentStart && segmentEnd) {
          addPoint(
            segmentStart.a,
            segmentStart.b,
            segmentStart.c,
            ax,
            ay,
            az,
            bx,
            by,
            bz,
            cx,
            cy,
            cz,
            outputArray
          )
          addPoint(
            segmentEnd.a,
            segmentEnd.b,
            segmentEnd.c,
            ax,
            ay,
            az,
            bx,
            by,
            bz,
            cx,
            cy,
            cz,
            outputArray
          )
        }
      }
    }
    startIndex = shapeIndexes[j]
    shapeIndexes[j] = outputArray.length / 6 // we want segment indices, not array indices
  }
}

// A number of "global" variables used to prevent frequent reallocation
// of Vec2 arrays, etc. If Javascript was multi-threaded, we'd need a better
// solution, but instead we'll borrow this paradigm from ThreeJS itself.
const uvs = new Array<number>()

// flattened array of 3d coordinates

const addPoint = (
  a: number,
  b: number,
  c: number,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
  outputArray: number[]
): void => {
  const x = a * ax + b * bx + c * cx
  const y = a * ay + b * by + c * cy
  const z = a * az + b * bz + c * cz
  outputArray.push(x, y, z)
}

const equalPoints = (
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number
) => {
  return (
    Math.abs(x1 - x2) < 1e-10 &&
    Math.abs(y1 - y2) < 1e-10 &&
    Math.abs(z1 - z2) < 1e-10
  )
}

/**
 * Converts path to uvs and appends to the end of the list uvs.
 * Takes wrapping and including boundary segments into consideration.
 * @param path path to draw
 * @param bounds bounds of component path exists on
 * @param wrapsX whether component wraps x
 * @param wrapsY whether component wraps y
 * @param createBoundaryLines if a line exists on boundary, then draws on both sides of the boundary
 *                            (for 3D component in 2D mode)
 * @param uvs list of uvs to append to
 */
const convertAndAppendPathToUvs = (
  path: GeckoComponentPath,
  bounds: GeckoComponentRect,
  wrapsX: boolean,
  wrapsY: boolean,
  createBoundaryLines: boolean,
  uvs: number[]
) => {
  let convertedS = convertCoordsToUVs(path.points[0], bounds)
  for (let i = 0; i < path.points.length - 1; i++) {
    const s = path.points[i]
    const e = path.points[i + 1]
    const convertedE = convertCoordsToUVs(e, bounds)
    if (s.componentID !== bounds.componentID) {
      convertedS = convertedE
      continue
    }
    addUVSegmentAndWrapProperly(
      ...convertedS,
      ...convertedE,
      uvs,
      path.pathDirectionX[i],
      path.pathDirectionY[i],
      wrapsX,
      wrapsY,
      createBoundaryLines
    )
    convertedS = convertedE
  }
}
